// GLTF model registry. Everything under public/models/ is generated by
// `npm run assets` and committed, so this never needs Blender at runtime.

import {
  type BufferGeometry,
  type Material,
  Mesh,
  type Texture,
  TextureLoader,
  SRGBColorSpace
} from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

import {
  BISHOP,
  KING,
  KNIGHT,
  PAWN,
  type PieceType,
  QUEEN,
  ROOK
} from "src/shared/board";
import { DAM, MARSHAL } from "src/shared/variants";

/**
 * File stem per rank, as `tools/buildPieces.mjs` writes them: one GLB per
 * piece under `public/models/pieces/`, each holding a single mesh node named
 * for its stem.
 */
const PIECE_FILE: Record<PieceType, string> = {
  [PAWN]: "pawn",
  [KNIGHT]: "knight",
  [BISHOP]: "bishop",
  [ROOK]: "rook",
  [QUEEN]: "queen",
  [KING]: "king"
};

/**
 * The variants that own a body, by `PIECE_VARIANTS` id.
 *
 * A variant without a file here draws as its base rank — the turbo pair, the
 * Joan and the crowned ranks all do — so the table is the list of what the
 * artist has made, not of what the rules know.
 */
const VARIANT_FILE: ReadonlyMap<number, string> = new Map([
  [MARSHAL, "pawn-marshal"],
  [DAM, "pawn-dam"]
]);

const pieceUrl = (stem: string) => `${import.meta.env.BASE_URL}models/pieces/${stem}.glb`;

export type PieceGeometries = Record<PieceType, BufferGeometry>;
/** A variant's own body, where it has one. See `VARIANT_FILE`. */
export type VariantGeometries = ReadonlyMap<number, BufferGeometry>;

/**
 * Piece sets.
 *
 * `"base"` is the set the game ships with, cut from `assets/Chess_Pieces.blend`
 * by `npm run assets:pieces`. `"classic"` is the carved set it replaced, kept
 * as the one pack: a single GLB re-materialled in palette colours, so the
 * armies are coloured exactly as the base set's are. Both are geometry only —
 * the renderer supplies the materials (PLAN §5.7.4), which is what lets a
 * third army cost a colour rather than a model.
 */
export const PIECE_PACKS = ["base", "classic"] as const;
export type PiecePack = (typeof PIECE_PACKS)[number];

/**
 * The pack the game boots with, from `?pack=`.
 *
 * A URL parameter rather than a setting, because what it is for is *looking at
 * a build* — the same reason `?room=` exists. Read once at startup; changing it
 * afterwards is what the dev menu is for.
 *
 * Validated against the table rather than trusted: a stale link naming a pack
 * that has since been renamed should boot the base set, not an empty board.
 */
export function packFromUrl(search = window.location.search): PiecePack | null {
  const named = new URLSearchParams(search).get("pack");
  return PIECE_PACKS.includes(named as PiecePack) ? (named as PiecePack) : null;
}

/**
 * A pack's own idea of "front", as a yaw to correct for, in radians.
 *
 * Empty, and that is the contract: **a set faces north (−Z) in its own file**.
 * `tools/exportPieces.py` turns the base set that way on export, and the
 * classic set is bodies of revolution but for its knight, which `PIECE_YAW`
 * names. A pack authored facing elsewhere gets its entry here rather than a
 * correction in the renderer.
 */
export const PACK_YAW: Partial<Record<PiecePack, number>> = {};

/**
 * Per-rank corrections, on top of the pack's own.
 *
 * **Read off a top-down render, not off the mesh's mass.** Centroid lean
 * cannot tell a knight's mane from its muzzle, and at the board's own 44°
 * pitch a knight reads equally well facing away or facing left. From directly
 * above a facing is unambiguous, and Blender's Z rotation is this renderer's
 * Y rotation, so a number read off such a render is typed straight in here.
 * Solve `PACK_YAW + PIECE_YAW = the model's own yaw` and the piece faces north.
 */
export const PIECE_YAW: Partial<Record<PiecePack, Partial<Record<PieceType, number>>>> = {
  classic: {
    // The one carved piece that is not a body of revolution. Its nose points
    // along +Z — measured off the geometry: the head's vertices average
    // z = +0.21 with x ≈ 0 — and the board's forward is −Z.
    [KNIGHT]: Math.PI
  }
};

/**
 * How hard a pack's paint mask is pushed, as a gain on its coverage.
 *
 * The mask is a *measurement* — how white the artist made this texel — and
 * this is the separate question of how much of that to spend, so it lives here
 * rather than being baked into the PNG. No shipping pack carries a mask; see
 * `LoadedPack.masks`.
 */
export const PACK_PAINT: Partial<Record<PiecePack, number>> = {};

const CLASSIC_URL = `${import.meta.env.BASE_URL}models/chess-pieces.glb`;

/** The classic set's node names, as authored in its .blend and preserved by `tools/exportChess.py`. */
const CLASSIC_NODE: Record<PieceType, string> = {
  [PAWN]: "Pawn",
  [KNIGHT]: "Knight",
  [BISHOP]: "Bishop",
  [ROOK]: "Rook",
  [QUEEN]: "Queen",
  [KING]: "King"
};

/** Kenney models the factory layer draws from, by file stem. */
export type KenneyName =
  | "conveyor"
  | "box-small"
  | "box-large"
  | "cover-hopper"
  | "robot-arm-a";

export interface KenneyModel {
  geometry: BufferGeometry;
  material: Material;
}

let cache: Promise<PieceGeometries> | undefined;
let variantCache: Promise<VariantGeometries> | undefined;
let kenneyCache: Promise<Record<KenneyName, KenneyModel>> | undefined;

/**
 * One piece file's body.
 *
 * The node transform is baked in, the way every other model here is handled:
 * the exporter leaves identity, and the renderer places a piece by its
 * square's centre with no per-model offset either way.
 */
async function loadBody(loader: GLTFLoader, stem: string): Promise<BufferGeometry> {
  const gltf = await loader.loadAsync(pieceUrl(stem));
  const node = gltf.scene.getObjectByName(stem);
  if (!(node instanceof Mesh)) {
    throw new Error(`pieces/${stem}.glb has no mesh "${stem}" — run npm run assets:pieces`);
  }
  node.updateWorldMatrix(true, false);
  const geometry = node.geometry.clone();
  geometry.applyMatrix4(node.matrixWorld);
  return geometry;
}

/**
 * Load the piece bodies once and share them. Materials are *not* taken from
 * the files — there are none to take; the renderer supplies palette materials
 * (PLAN §5.7.4), so two sides cost two materials rather than two model files.
 */
export function loadPieceGeometries(): Promise<PieceGeometries> {
  cache ??= (async () => {
    const loader = new GLTFLoader();
    // One load per file rather than per rank.
    const stems = [...new Set(Object.values(PIECE_FILE))];
    const bodies = new Map(
      await Promise.all(stems.map(async (stem) => [stem, await loadBody(loader, stem)] as const))
    );
    const out = {} as PieceGeometries;
    for (const [type, stem] of Object.entries(PIECE_FILE)) {
      out[Number(type) as PieceType] = bodies.get(stem)!;
    }
    return out;
  })();
  return cache;
}

/**
 * The variants' own bodies.
 *
 * **A missing file is a gap in the art, not an error.** The variant draws as
 * its base rank, which is what every variant without a body already does, and
 * the gap is named in the console rather than thrown — a set arrives a piece
 * at a time.
 */
export function loadVariantGeometries(): Promise<VariantGeometries> {
  variantCache ??= (async () => {
    const loader = new GLTFLoader();
    const out = new Map<number, BufferGeometry>();
    await Promise.all(
      [...VARIANT_FILE].map(async ([id, stem]) => {
        try {
          out.set(id, await loadBody(loader, stem));
        } catch {
          console.warn(`[models] no body for variant ${id} (${stem}.glb); drawing its base rank`);
        }
      })
    );
    return out;
  })();
  return variantCache;
}

const packCache = new Map<PiecePack, Promise<LoadedPack>>();

/**
 * A loaded piece set: the bodies, and whatever else the pack brings.
 *
 * Only the geometries are ever present today. The rest is the shape a pack
 * with baked art takes — `PieceViews` tints such materials per side rather
 * than replacing them, `armyTint.ts` paints where a mask says, and
 * `IndustryViews` swaps the kit's conveyor for a pack's own — and it stays so
 * that the next pack made that way is a table entry, not a renderer change.
 */
export interface LoadedPack {
  geometries: PieceGeometries;
  /**
   * The variants' bodies, for a pack that has them. Absent for `classic`,
   * whose variants draw as their base rank.
   */
  variants?: VariantGeometries;
  /** The pack's own materials, per rank, for a pack whose art is baked in. */
  materials?: Partial<Record<PieceType, Material>>;
  /**
   * How much of each texel the army's colour reaches, per rank — a coverage
   * map written beside the model, sampled on the same UVs as its base map,
   * `flipY` false and `NoColorSpace` (a coverage is a number, not a colour).
   */
  masks?: Partial<Record<PieceType, Texture>>;
  /** The pack's own conveyor, for a pack that ships one. See `PackBelts`. */
  belts?: PackBelts;
}

/**
 * A pack's own belt parts, replacing Kenney's for the whole board.
 *
 * **Geometry only, deliberately.** A belt is tinted per side by
 * `IndustryViews`, because whose lane it is decides territory, who it carries
 * and what crushing it costs; a baked texture would fight the one thing a
 * belt's colour has to say. The corner is optional on its own: without one the
 * straight is bent through a quarter turn exactly as Kenney's is
 * (`bendConveyor`).
 */
export interface PackBelts {
  straight: BufferGeometry;
  corner?: BufferGeometry;
}

/**
 * The classic set: one GLB, six named nodes, no materials worth keeping.
 *
 * Exported nodes sit at the origin with identity transforms and their base on
 * Y=0 (`tools/exportChess.py`), so the geometry is usable as-is.
 */
async function loadClassic(): Promise<LoadedPack> {
  const gltf = await new GLTFLoader().loadAsync(CLASSIC_URL);
  const geometries = {} as PieceGeometries;
  for (const [type, name] of Object.entries(CLASSIC_NODE)) {
    const node = gltf.scene.getObjectByName(name);
    if (!(node instanceof Mesh)) {
      throw new Error(`chess-pieces.glb is missing mesh "${name}" — run npm run assets`);
    }
    geometries[Number(type) as PieceType] = node.geometry;
  }
  return { geometries };
}

export function loadPiecePack(pack: PiecePack): Promise<LoadedPack> {
  if (pack === "base") {
    return Promise.all([loadPieceGeometries(), loadVariantGeometries()]).then(
      ([geometries, variants]) => ({ geometries, variants })
    );
  }
  const cached = packCache.get(pack);
  if (cached) return cached;
  const promise = loadClassic();
  packCache.set(pack, promise);
  return promise;
}

const KENNEY_NAMES: readonly KenneyName[] = [
  "conveyor",
  "box-small",
  "box-large",
  "cover-hopper",
  "robot-arm-a"
];

/**
 * Kenney's kit, loaded with its own materials intact — unlike the chess set,
 * which we re-material. Every model in the kit shares one `Textures/colormap.png`
 * atlas referenced by a relative URI, which is why the pipeline preserves that
 * path on copy (tools/buildAssets.mjs).
 */
export function loadKenneyModels(): Promise<Record<KenneyName, KenneyModel>> {
  kenneyCache ??= (async () => {
    const loader = new GLTFLoader();
    const out = {} as Record<KenneyName, KenneyModel>;

    await Promise.all(
      KENNEY_NAMES.map(async (name) => {
        const gltf = await loader.loadAsync(
          `${import.meta.env.BASE_URL}models/kenney/${name}.glb`
        );
        let found: Mesh | undefined;
        gltf.scene.traverse((node) => {
          if (!found && node instanceof Mesh) found = node;
        });
        if (!found) throw new Error(`no mesh in ${name}.glb — run npm run assets`);

        // Bake the node's world transform into the geometry. Kenney's exports
        // carry scale and offset on the *node*, not the mesh data, so lifting
        // `geometry` out on its own and instancing it renders at the wrong
        // size and in the wrong place — a conveyor comes out as an oversized
        // slab straddling two squares.
        gltf.scene.updateWorldMatrix(true, true);
        const geometry = found.geometry.clone();
        geometry.applyMatrix4(found.matrixWorld);

        // Kenney authors conveyors running along X; this project's facing
        // convention points a direction-0 part along -Z (coords.ts). Without
        // this the whole belt network renders ninety degrees off its own
        // direction data — a north lane draws as an east-west one — which
        // looks like a rules bug and is not.
        //
        // Baked into the geometry here rather than applied per instance,
        // beside the existing node-transform bake, so `FACING_YAW[dir]` stays
        // the single expression of direction everywhere in src/client/ai/render.
        if (name.startsWith("conveyor")) geometry.rotateY(Math.PI / 2);

        geometry.computeVertexNormals();

        out[name] = { geometry, material: found.material as Material };
      })
    );

    return out;
  })();

  return kenneyCache;
}

let propCache: Promise<PropGeometries> | undefined;
let ingotTextureCache: Promise<Texture[]> | undefined;

export interface PropGeometries {
  /** The heap a capture leaves. Untextured — see assets/LICENSES.md. */
  /** One unit of material. Keeps its UVs, so it can wear a grade's texture. */
  ingot: BufferGeometry;
  /**
   * The turret, with **its material as well as its geometry**.
   *
   * The one prop here that keeps what it was authored with. Everything else on
   * this board is re-materialled into the Kenney palette or tinted by grade,
   * because it stands for something the rules know about — a unit, a heap, a
   * belt — and its colour is what says which. A turret is a *building*, and
   * what a building is made of is the whole of what makes it read as built:
   * stripped to a palette colour it is a grey cylinder, which is the crate
   * problem PLAN §14.8 already names.
   *
   * One mesh and one material, which is what lets it be instanced — checked
   * against the file rather than assumed, since a joined FBX easily arrives
   * with a material slot per original object and an `InstancedMesh` has room
   * for exactly one.
   */
  turret: { geometry: BufferGeometry; material: Material };
}

/**
 * The two purchased props, as bare geometry.
 *
 * Both are normalised by the asset pipeline to sit on Y=0 at board scale
 * (`tools/decimate.py`, `tools/convertProp.py`), so like the chess set they
 * are usable as-is and get this project's own materials.
 */
/**
 * The ingot mesh's own dimensions, measured off the geometry.
 *
 * A bar, not a cube: **1.0 long, 0.2629 tall, 0.3865 deep**, and its origin sits
 * at the *front* Z edge rather than in the middle (the box runs −0.3865..0), so
 * placing one at a point puts it behind that point by half its depth.
 *
 * These are properties of the *art*, like `KNIGHT_MODEL_FORWARD`, and the pile
 * layout is derived from them rather than tuned against them by eye. It was
 * tuned by eye: the layer step was 0.62 against a real height of 0.2629, so
 * every stack floated with a gap of a third of an ingot between each layer, and
 * the in-plane steps left a similar space to the side. Re-measure with
 * `geometry.computeBoundingBox()` if `ingot.glb` is ever rebuilt.
 */
export const INGOT_EXTENT = { x: 1, y: 0.2629, z: 0.3865 } as const;
/** How far the mesh's origin sits from its own centre, along Z. */
export const INGOT_CENTRE_Z = -0.1933;

export function loadProps(): Promise<PropGeometries> {
  propCache ??= (async () => {
    const loader = new GLTFLoader();
    const base = import.meta.env.BASE_URL;

    const pick = async (file: string): Promise<BufferGeometry> => {
      const gltf = await loader.loadAsync(`${base}models/${file}`);
      let found: Mesh | undefined;
      gltf.scene.traverse((node) => {
        if (!found && node instanceof Mesh) found = node;
      });
      if (!found) throw new Error(`no mesh in ${file} — run npm run assets`);
      found.updateWorldMatrix(true, false);
      const geometry = found.geometry.clone();
      geometry.applyMatrix4(found.matrixWorld);
      return geometry;
    };

    // The scrap heap model went with the cone: material on the board is dead
    // pieces and ingots now, and a shape nothing draws is a download nobody
    // needs.
    const ingot = await pick("ingot.glb");

    // The turret keeps its own material, so it cannot go through `pick`.
    const gltf = await loader.loadAsync(`${base}models/turret.glb`);
    let gun: Mesh | undefined;
    gltf.scene.traverse((node) => {
      if (!gun && node instanceof Mesh) gun = node;
    });
    if (!gun) throw new Error("no mesh in turret.glb — run npm run assets");
    gun.updateWorldMatrix(true, false);
    const gunGeometry = gun.geometry.clone();
    gunGeometry.applyMatrix4(gun.matrixWorld);
    const gunMaterial = Array.isArray(gun.material) ? gun.material[0] : gun.material;

    return { ingot, turret: { geometry: gunGeometry, material: gunMaterial } };
  })();

  return propCache;
}

/**
 * One base-colour map per grade: iron, silver, gold, diamond.
 *
 * 256px each, which is what a unit occupies on screen and no more — the source
 * pack was 60 MB of maps this never needed (assets/LICENSES.md).
 */
export function loadIngotTextures(): Promise<Texture[]> {
  ingotTextureCache ??= (async () => {
    const loader = new TextureLoader();
    const base = import.meta.env.BASE_URL;
    const names = ["iron", "silver", "gold", "diamond"];
    return Promise.all(
      names.map(async (name) => {
        const texture = await loader.loadAsync(`${base}models/ingots/${name}.jpg`);
        texture.colorSpace = SRGBColorSpace;
        texture.flipY = false;
        return texture;
      })
    );
  })();

  return ingotTextureCache;
}
